import { useTranslation } from "react-i18next";

import { TooltipPin } from "@/assets/icons/ds";
import { useGetLinks } from "@/components/FriendsWithBenefits/SocialLinks";
import { ButtonAnchor } from "@/components/Interface/Button/Button";

interface SharePanelProps {
  dataTestid?: string;
  invitationLink?: string;
}

/**
 * The share targets of the invitation link, shown in the dark panel that hangs
 * off the share button. Rendered as plain anchors - every target is an external
 * URL, which neither `Button` nor `ButtonLink` (react-router) covers - but with
 * small inverted button styling.
 */
export const SharePanel = ({ dataTestid, invitationLink }: SharePanelProps) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits.groupOverview",
  });

  const shareText = `${t("shareSubHeadline").replace(/<br\s*\/?>/g, "\n")}\n\n${t("shareText")}\n`;
  const links = useGetLinks(t("shareTitle"), shareText, invitationLink);

  return (
    <div data-testid={dataTestid} className="flex w-60 flex-col">
      <TooltipPin className="mr-2 h-2 w-6 self-end text-surface-primary" />
      <div className="flex flex-col gap-4 rounded-lg bg-surface-primary p-4">
        <div className="flex flex-col gap-4">
          <p className="text-body-base font-semibold leading-xs text-foreground-primary-inverse">
            {t("sharePanel.title")}
          </p>
          <p className="text-body-xs leading-none text-foreground-secondary-inverse">
            {t("sharePanel.subtitle")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {links.map(({ label, icon, link }) => (
            <ButtonAnchor
              key={label}
              href={link}
              icon={icon}
              inverted
              size="sm"
              variant="secondary"
            >
              {label}
            </ButtonAnchor>
          ))}
        </div>
      </div>
    </div>
  );
};
